// Wave 3.99ae — Internal mail helpers (attachments, formatting, download).
import DOMPurify from 'dompurify';


export const MAIL_ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx';
export const MAX_MAIL_ATTACH_BYTES = 2 * 1024 * 1024; // 2MB per file

// Map file extension -> the exact MIME the backend allowlist expects. We derive
// the MIME from the extension (browsers are inconsistent for Office files),
// then rebuild the data URL header so backend validation always passes.
const EXT_MIME = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  webp: 'image/webp',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

export function extOf(name = '') {
  const p = name.split('.');
  return p.length > 1 ? p.pop().toLowerCase() : '';
}

function readFileAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

// Validate + convert a File to the { name, mime, data_url } shape the API wants.
// Throws Error with a user-friendly message on unsupported type / oversize.
export async function fileToAttachment(file) {
  const ext = extOf(file.name);
  const mime = EXT_MIME[ext];
  if (!mime) throw new Error(`${file.name}: unsupported type (allowed: PDF, image, Word, Excel)`);
  if (file.size > MAX_MAIL_ATTACH_BYTES) throw new Error(`${file.name}: exceeds 2MB`);
  const raw = await readFileAsDataURL(file);
  const comma = raw.indexOf(',');
  const payload = comma >= 0 ? raw.slice(comma + 1) : raw;
  return { name: file.name, mime, size: file.size, data_url: `data:${mime};base64,${payload}` };
}

export function prettySize(bytes = 0) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// A short human label for a mime/extension, used on attachment chips.
export function fileKind(mime = '', name = '') {
  const m = (mime || '').toLowerCase();
  const ext = extOf(name);
  if (m.includes('pdf') || ext === 'pdf') return 'PDF';
  if (m.startsWith('image/') || ['png', 'jpg', 'jpeg', 'webp'].includes(ext)) return 'Image';
  if (m.includes('word') || m.includes('msword') || ['doc', 'docx'].includes(ext)) return 'Word';
  if (m.includes('excel') || m.includes('spreadsheet') || ['xls', 'xlsx'].includes(ext)) return 'Excel';
  return 'File';
}

export function downloadDataUrl(name, dataUrl) {
  try {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = name || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch { /* noop */ }
}

// Max characters allowed in a rich-text mail body (guards the MongoDB 16MB
// document limit once base64 images are inlined). ~12MB of HTML text.
export const MAX_MAIL_BODY_CHARS = 12 * 1024 * 1024;

// True if a File/Blob is an image — tolerant of a missing MIME type (some
// browsers / programmatic inputs leave ``type`` blank), falling back to the
// filename extension.
export function isImageFile(file) {
  if (!file) return false;
  if ((file.type || '').startsWith('image/')) return true;
  return /\.(png|jpe?g|webp|gif|bmp)$/i.test(file.name || '');
}

// Turn an image File/Blob into a DOWNSCALED, compressed data URL suitable for
// inlining inside the rich-text mail body. Large pasted/photographed images are
// shrunk to ``maxDim`` on the longest edge and re-encoded (JPEG for opaque,
// PNG kept for anything with transparency) so the stored HTML stays small.
export function imageToInlineDataUrl(file, { maxDim = 1280, quality = 0.82 } = {}) {
  return new Promise((resolve, reject) => {
    if (!isImageFile(file)) {
      reject(new Error('Not an image'));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read image'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Could not decode image'));
      img.onload = () => {
        try {
          let { width, height } = img;
          const scale = Math.min(1, maxDim / Math.max(width, height));
          width = Math.max(1, Math.round(width * scale));
          height = Math.max(1, Math.round(height * scale));
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          // PNG preserves transparency; everything else → JPEG (smaller).
          const isPng = (file.type || '').includes('png') || /\.png$/i.test(file.name || '');
          const out = isPng
            ? canvas.toDataURL('image/png')
            : canvas.toDataURL('image/jpeg', quality);
          resolve(out);
        } catch (e) {
          reject(e);
        }
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// Strip HTML → plain text (client-side), for compose emptiness checks and any
// short preview needs. Never used for rendering.
export function htmlToText(html = '') {
  try {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return (div.textContent || div.innerText || '').replace(/\u00a0/g, ' ').trim();
  } catch {
    return (html || '').replace(/<[^>]*>/g, ' ').trim();
  }
}

// --- Rich-body rendering ---------------------------------------------------
// Single source of truth for the internal-mail HTML sanitisation policy so
// every surface that renders a mail body (viewer dialog, Super Admin login
// popup, …) shows the SAME sanitized rich text the sender composed.
const MAIL_ALLOWED_TAGS = [
  'p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'span', 'div',
  'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5',
  'h6', 'pre', 'code', 'font', 'hr',
];
const MAIL_ALLOWED_ATTR = ['href', 'src', 'alt', 'title', 'style', 'target', 'rel', 'width', 'height', 'color', 'class'];

// Allows the formatting tags the Compose editor emits (bold / colour / lists /
// links) plus inline images (base64 data URIs). Everything else — scripts,
// event handlers — is stripped. Requires ``dompurify``.
export function sanitizeMailHtml(body) {
  return DOMPurify.sanitize(body || '', {
    ALLOWED_TAGS: MAIL_ALLOWED_TAGS,
    ALLOWED_ATTR: MAIL_ALLOWED_ATTR,
  });
}

// New rich bodies contain markup; legacy plain-text bodies do not.
export function looksLikeHtml(s) {
  return /<[a-z][\s\S]*>/i.test(s || '');
}
